import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { STATUS_BADGE, eur } from './GestionLocativePage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]';
const label = 'block text-xs font-semibold text-gray-600 mb-1';
const periodLabel = (p) => {
  const [y, m] = (p || '').split('-');
  const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  return m ? `${months[parseInt(m, 10) - 1]} ${y}` : p;
};

const RentModal = ({ prop, payment, onClose, onDone }) => {
  const now = new Date();
  const isEdit = !!payment;
  const [period, setPeriod] = useState(payment?.period || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [partial, setPartial] = useState(!!(payment?.date_from && payment?.date_to));
  const [dateFrom, setDateFrom] = useState(payment?.date_from || '');
  const [dateTo, setDateTo] = useState(payment?.date_to || '');
  const [amount, setAmount] = useState(String(payment?.rent_received ?? prop.rent_amount));
  const [amountTouched, setAmountTouched] = useState(isEdit);
  const [dateReceived, setDateReceived] = useState(payment?.date_received || now.toISOString().slice(0, 10));
  const [sendEmails, setSendEmails] = useState(!isEdit);
  const [saving, setSaving] = useState(false);

  const daysInMonth = (p) => {
    const [y, m] = p.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  };
  const monthBounds = (p) => [`${p}-01`, `${p}-${String(daysInMonth(p)).padStart(2, '0')}`];

  const ratio = (() => {
    if (!partial || !dateFrom || !dateTo) return 1;
    const days = Math.max((new Date(dateTo) - new Date(dateFrom)) / 86400000 + 1, 1);
    return Math.min(days / daysInMonth(period), 1);
  })();

  const togglePartial = (checked) => {
    setPartial(checked);
    if (checked && (!dateFrom || !dateTo)) {
      const [f, t] = monthBounds(period);
      setDateFrom(f); setDateTo(t);
    }
    if (!checked && !amountTouched) setAmount(String(prop.rent_amount));
  };

  useEffect(() => {
    if (partial && !amountTouched && dateFrom && dateTo) {
      setAmount(String(Math.round(prop.rent_amount * ratio * 100) / 100));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, partial, period]);

  const amt = parseFloat(amount) || 0;
  const noMgmt = !!prop.no_management;
  const mult = prop.pct_mode === 'ttc' ? 1 : 1.2;
  const modeLbl = prop.pct_mode === 'ttc' ? 'TTC' : 'HT';
  const fees = noMgmt ? 0 : Math.max(amt * (prop.mgmt_pct || 0) / 100 * mult, (prop.mgmt_min || 0) * ratio);
  const gli = (!noMgmt && prop.gli_enabled) ? amt * (prop.gli_rate_pct || 0) / 100 * mult : 0;
  const net = amt - fees - gli;

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        period, amount: amt, date_received: dateReceived, send_emails: sendEmails,
        date_from: partial ? dateFrom : '', date_to: partial ? dateTo : '',
      };
      if (isEdit) {
        await axios.put(`${API}/gestion/payments/${payment.payment_id}`, body);
        toast.success(`Encaissement modifié — documents régénérés${sendEmails ? ' et renvoyés' : ''}`);
      } else {
        await axios.post(`${API}/gestion/properties/${prop.property_id}/rent-received`, body);
        toast.success(`Loyer encaissé — documents générés${sendEmails ? ' et envoyés' : ''}`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rent-received-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{isEdit ? "Modifier l'encaissement" : 'Loyer encaissé'}</h2>
        <div className="space-y-3">
          <div><label className={label}>Période</label><input type="month" className={input} value={period} onChange={e => setPeriod(e.target.value)} data-testid="rent-period-input" /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={partial} onChange={e => togglePartial(e.target.checked)} className="accent-[#008A8D]" data-testid="rent-partial-checkbox" />
            Mois incomplet (arrivée/départ en cours de mois — prorata)
          </label>
          {partial && (
            <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-3">
              <div><label className={label}>Du</label><input type="date" className={input} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setAmountTouched(false); }} data-testid="rent-date-from" /></div>
              <div><label className={label}>Au</label><input type="date" className={input} value={dateTo} onChange={e => { setDateTo(e.target.value); setAmountTouched(false); }} data-testid="rent-date-to" /></div>
              <p className="col-span-2 text-[11px] text-gray-500">Prorata automatique : {Math.round(ratio * 1000) / 10} % du loyer ({eur(prop.rent_amount)}) — montant modifiable ci-dessous.</p>
            </div>
          )}
          <div><label className={label}>Montant reçu (€)</label><input type="number" step="0.01" className={input} value={amount} onChange={e => { setAmount(e.target.value); setAmountTouched(true); }} data-testid="rent-amount-received-input" /></div>
          <div><label className={label}>Date d'encaissement</label><input type="date" className={input} value={dateReceived} onChange={e => setDateReceived(e.target.value)} /></div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1" data-testid="rent-calc-preview">
            <div className="flex justify-between"><span className="text-gray-500">Loyer reçu</span><b>{eur(amount)}</b></div>
            {noMgmt ? (
              <div className="flex justify-between"><span className="text-gray-500">Bien sans gestion</span><b className="text-gray-400">0,00 € d'honoraires</b></div>
            ) : (
              <div className="flex justify-between"><span className="text-gray-500">Honoraires TTC ({prop.mgmt_pct}% {modeLbl} — min {eur((prop.mgmt_min || 0) * ratio)})</span><b className="text-red-600">- {eur(fees)}</b></div>
            )}
            {gli > 0 && <div className="flex justify-between"><span className="text-gray-500">Garantie loyers impayés ({prop.gli_rate_pct}% {modeLbl})</span><b className="text-red-600">- {eur(gli)}</b></div>}
            <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-semibold">Net à reverser</span><b className="text-[#008A8D]">{eur(net)}</b></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={sendEmails} onChange={e => setSendEmails(e.target.checked)} className="accent-[#008A8D]" data-testid="rent-send-emails-checkbox" />
            {isEdit ? 'Renvoyer les documents corrigés par email' : noMgmt ? 'Envoyer la quittance au locataire' : 'Envoyer quittance (locataire) + compte rendu et facture (propriétaire)'}
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={submit} disabled={saving} className="px-6 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="confirm-rent-received-btn">
            {saving ? 'Traitement...' : isEdit ? 'Enregistrer les corrections' : "Valider l'encaissement"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RevisionModal = ({ prop, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [irlNew, setIrlNew] = useState('');
  const [irlOld, setIrlOld] = useState('');
  const [qNew, setQNew] = useState('');
  const [qOld, setQOld] = useState('');
  const [effDate, setEffDate] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gestion/properties/${prop.property_id}/rent-revision/preview`)
      .then(r => {
        const p = r.data;
        setPreview(p);
        setIrlNew(p.irl_new_value != null ? String(p.irl_new_value) : '');
        setIrlOld(p.irl_old_value != null ? String(p.irl_old_value) : '');
        setQNew(p.irl_new_quarter || '');
        setQOld(p.irl_old_quarter || '');
        setEffDate(p.effective_date || '');
      })
      .catch(() => toast.error('Impossible de charger les indices IRL'))
      .finally(() => setLoading(false));
  }, [prop.property_id]);

  const hc = preview ? preview.rent_hc : 0;
  const charges = preview ? preview.charges : 0;
  const nv = parseFloat(irlNew) || 0;
  const ov = parseFloat(irlOld) || 0;
  const newHc = ov > 0 && nv > 0 ? Math.round(hc * nv / ov * 100) / 100 : null;
  const pct = newHc != null && hc > 0 ? (newHc / hc - 1) * 100 : null;

  const submit = async () => {
    if (!newHc || !qNew.trim() || !qOld.trim()) { toast.error('Indices et trimestres IRL requis'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/gestion/properties/${prop.property_id}/rent-revision`, {
        irl_new_quarter: qNew, irl_new_value: nv,
        irl_old_quarter: qOld, irl_old_value: ov,
        effective_date: effDate || '', send_email: sendEmail,
      });
      toast.success(`Loyer révisé${r.data.revision.letter_sent ? ' — courrier envoyé au locataire' : ''}`);
      onDone(r.data.property);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="revision-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Réviser le loyer (IRL)</h2>
        <p className="text-xs text-gray-500 mb-4">Nouveau loyer HC = loyer HC actuel × (nouvel IRL ÷ IRL du même trimestre l'année précédente). Indices INSEE pré-remplis, modifiables.</p>
        {loading ? <p className="text-sm text-gray-400">Récupération des indices INSEE...</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Nouvel IRL — trimestre</label><input className={input} value={qNew} onChange={e => setQNew(e.target.value)} placeholder="ex : 1er trimestre 2026" data-testid="revision-new-quarter" /></div>
              <div><label className={label}>Valeur du nouvel indice</label><input type="number" step="0.01" className={input} value={irlNew} onChange={e => setIrlNew(e.target.value)} data-testid="revision-new-value" /></div>
              <div><label className={label}>IRL de référence (N-1)</label><input className={input} value={qOld} onChange={e => setQOld(e.target.value)} placeholder="ex : 1er trimestre 2025" data-testid="revision-old-quarter" /></div>
              <div><label className={label}>Valeur de l'indice N-1</label><input type="number" step="0.01" className={input} value={irlOld} onChange={e => setIrlOld(e.target.value)} data-testid="revision-old-value" /></div>
            </div>
            <div><label className={label}>Date d'effet (anniversaire du bail)</label><input type="date" className={input} value={effDate} onChange={e => setEffDate(e.target.value)} data-testid="revision-effective-date" />
              {preview?.lease_base_date && <p className="text-[11px] text-gray-400 mt-1">Bail signé / entrée le {preview.lease_base_date.split('-').reverse().join('/')}</p>}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1" data-testid="revision-preview">
              <div className="flex justify-between"><span className="text-gray-500">Loyer HC actuel</span><b>{eur(hc)}</b></div>
              <div className="flex justify-between"><span className="text-gray-500">Nouveau loyer HC</span><b className="text-[#008A8D]">{newHc != null ? eur(newHc) : '—'}</b></div>
              {pct != null && <div className="flex justify-between"><span className="text-gray-500">Variation</span><b className={pct >= 0 ? 'text-orange-600' : 'text-green-600'}>{pct >= 0 ? '+' : ''}{pct.toFixed(2)} %</b></div>}
              <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-semibold">Nouveau loyer CC (charges {eur(charges)})</span><b className="text-[#008A8D]">{newHc != null ? eur(Math.round((newHc + charges) * 100) / 100) : '—'}</b></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="accent-[#008A8D]" data-testid="revision-send-email" />
              Envoyer le courrier de révision au locataire par email
            </label>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={submit} disabled={saving || loading} className="px-6 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="revision-submit-btn">
            {saving ? 'Traitement...' : 'Valider la révision'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OneOffInvoiceModal = ({ prop, onClose, onDone }) => {
  const [labelTxt, setLabelTxt] = useState('Honoraires de mise en place du locataire');
  const [amountTtc, setAmountTtc] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const ttc = parseFloat(amountTtc);
    if (!labelTxt.trim() || !ttc || ttc <= 0) { toast.error('Libellé et montant requis'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/gestion/properties/${prop.property_id}/invoices`,
        { label: labelTxt, amount_ttc: ttc, send_email: sendEmail });
      toast.success(`Facture ${r.data.number} générée${r.data.email_sent ? ' et envoyée' : ''}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="oneoff-invoice-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Facturer des honoraires</h2>
        <p className="text-xs text-gray-500 mb-4">Facture libre (ex : mise en place du locataire) — numérotation FACT automatique, TVA 20 % extraite.</p>
        <div className="space-y-3">
          <div><label className={label}>Libellé</label><input className={input} value={labelTxt} onChange={e => setLabelTxt(e.target.value)} data-testid="oneoff-label-input" /></div>
          <div><label className={label}>Montant TTC (€)</label><input type="number" step="0.01" className={input} value={amountTtc} onChange={e => setAmountTtc(e.target.value)} data-testid="oneoff-amount-input" /></div>
          {amountTtc > 0 && <p className="text-xs text-gray-500">Soit {eur(amountTtc / 1.2)} HT + {eur(amountTtc - amountTtc / 1.2)} TVA</p>}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="accent-[#008A8D]" data-testid="oneoff-send-email" />
            Envoyer la facture au propriétaire par email
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={submit} disabled={saving} className="px-6 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="oneoff-submit-btn">
            {saving ? 'Génération...' : 'Générer la facture'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditSection = ({ prop, onSaved }) => {
  const [form, setForm] = useState({ ...prop });
  const [editing, setEditing] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const r = await axios.put(`${API}/gestion/properties/${prop.property_id}`, {
        owner_name: form.owner_name, owner_company: form.owner_company, owner_email: form.owner_email,
        owner_phone: form.owner_phone, owner_iban: form.owner_iban || '', owner_bic: form.owner_bic || '',
        owner_address: form.owner_address || '',
        address: form.address, lot_label: form.lot_label || '', property_type: form.property_type || '',
        surface_m2: form.surface_m2 ? parseFloat(form.surface_m2) : null,
        irl_ref: form.irl_ref || '', zone_tendue: !!form.zone_tendue,
        rent_amount: parseFloat(form.rent_amount), charges_amount: parseFloat(form.charges_amount || 0),
        charges_detail: form.charges_detail, payment_mode: form.payment_mode,
        mgmt_pct: parseFloat(form.mgmt_pct), mgmt_min: parseFloat(form.mgmt_min),
        pct_mode: form.pct_mode || 'ht',
        no_management: !!form.no_management,
        gli_enabled: !!form.gli_enabled, gli_rate_pct: parseFloat(form.gli_rate_pct || 0),
        start_date: form.start_date, payment_day: parseInt(form.payment_day || 5, 10),
        notice_enabled: form.notice_enabled !== false, notice_days_before: parseInt(form.notice_days_before || 3, 10),
      });
      toast.success('Bien mis à jour');
      setEditing(false); onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="realease-card p-6" data-testid="property-info-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Bien & conditions de gestion</h3>
        <button onClick={() => editing ? save() : setEditing(true)} className="text-sm font-semibold text-[#008A8D]" data-testid="edit-property-btn">
          {editing ? 'Enregistrer' : 'Modifier'}
        </button>
      </div>
      {editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={label}>Adresse</label><input className={input} value={form.address} onChange={e => set('address', e.target.value)} /></div>
          <div><label className={label}>Propriétaire</label><input className={input} value={form.owner_name} onChange={e => set('owner_name', e.target.value)} /></div>
          <div><label className={label}>Société</label><input className={input} value={form.owner_company || ''} onChange={e => set('owner_company', e.target.value)} /></div>
          <div><label className={label}>Email propriétaire</label><input className={input} value={form.owner_email || ''} onChange={e => set('owner_email', e.target.value)} /></div>
          <div><label className={label}>Téléphone</label><input className={input} value={form.owner_phone || ''} onChange={e => set('owner_phone', e.target.value)} /></div>
          <div><label className={label}>IBAN propriétaire</label><input className={input} value={form.owner_iban || ''} onChange={e => set('owner_iban', e.target.value)} placeholder="FR76..." data-testid="edit-owner-iban" /></div>
          <div><label className={label}>BIC</label><input className={input} value={form.owner_bic || ''} onChange={e => set('owner_bic', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Adresse postale du propriétaire</label><input className={input} value={form.owner_address || ''} onChange={e => set('owner_address', e.target.value)} data-testid="edit-owner-address" /></div>
          <div><label className={label}>Lot (n° / référence)</label><input className={input} value={form.lot_label || ''} onChange={e => set('lot_label', e.target.value)} placeholder="Lot 60276" data-testid="edit-lot-label" /></div>
          <div><label className={label}>Type de bien</label><input className={input} value={form.property_type || ''} onChange={e => set('property_type', e.target.value)} placeholder="Maison 3 pièces" data-testid="edit-property-type" /></div>
          <div><label className={label}>Surface habitable (m²)</label><input className={input} type="number" step="0.01" value={form.surface_m2 || ''} onChange={e => set('surface_m2', e.target.value)} data-testid="edit-surface" /></div>
          <div><label className={label}>IRL — trimestre de référence</label><input className={input} value={form.irl_ref || ''} onChange={e => set('irl_ref', e.target.value)} placeholder="ex : 2e trimestre 2026" data-testid="edit-irl-ref" /></div>
          <div className="flex items-end pb-2"><label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!form.zone_tendue} onChange={e => set('zone_tendue', e.target.checked)} className="accent-[#008A8D]" data-testid="edit-zone-tendue" />
            Zone tendue (encadrement des loyers)
          </label></div>
          <div><label className={label}>Loyer CC (€)</label><input type="number" className={input} value={form.rent_amount} onChange={e => set('rent_amount', e.target.value)} /></div>
          <div><label className={label}>Dont charges (€)</label><input type="number" className={input} value={form.charges_amount || 0} onChange={e => set('charges_amount', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Détail charges</label><input className={input} value={form.charges_detail || ''} onChange={e => set('charges_detail', e.target.value)} /></div>
          <div><label className={label}>Mode de paiement</label>
            <select className={input} value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)}>
              <option value="virement">Virement</option><option value="prelevement">Prélèvement</option>
            </select></div>
          <div><label className={label}>Jour d'échéance</label><input type="number" min="1" max="28" className={input} value={form.payment_day} onChange={e => set('payment_day', e.target.value)} /></div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <input type="checkbox" checked={!!form.no_management} onChange={e => set('no_management', e.target.checked)} className="accent-[#008A8D]" data-testid="edit-no-management" />
              <span><b>Bien sans gestion</b> — quittances automatiques uniquement, 0 € d'honoraires, pas de compte rendu</span>
            </label>
          </div>
          <div><label className={label}>% gestion</label>
            <div className="flex gap-2">
              <input type="number" step="0.1" className={input} value={form.mgmt_pct} onChange={e => set('mgmt_pct', e.target.value)} disabled={!!form.no_management} data-testid="edit-mgmt-pct" />
              <select className={input + ' w-24'} value={form.pct_mode || 'ht'} onChange={e => set('pct_mode', e.target.value)} disabled={!!form.no_management} data-testid="edit-pct-mode">
                <option value="ht">% HT</option>
                <option value="ttc">% TTC</option>
              </select>
            </div>
          </div>
          <div><label className={label}>Minimum mensuel TTC (€)</label><input type="number" className={input} value={form.mgmt_min} onChange={e => set('mgmt_min', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Garantie Loyers Impayés (GLI)</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.gli_enabled} onChange={e => set('gli_enabled', e.target.checked)} className="accent-[#008A8D]" data-testid="edit-gli-enabled" />
              <span className="text-xs text-gray-600">Souscrite — taux</span>
              <input className={input + ' w-20'} type="number" step="0.1" value={form.gli_rate_pct ?? 2.5} onChange={e => set('gli_rate_pct', e.target.value)} disabled={!form.gli_enabled} data-testid="edit-gli-rate" />
              <span className="text-xs text-gray-500">% HT du loyer</span>
            </div>
          </div>
          <div><label className={label}>Début de gestion</label><input type="date" className={input} value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} /></div>
          <div><label className={label}>Avis d'échéance locataire</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.notice_enabled !== false} onChange={e => set('notice_enabled', e.target.checked)} className="accent-[#008A8D]" data-testid="edit-notice-enabled" />
              <input className={input + ' w-16'} type="number" min="1" max="15" value={form.notice_days_before ?? 3} onChange={e => set('notice_days_before', e.target.value)} data-testid="edit-notice-days" />
              <span className="text-xs text-gray-500">jours avant</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><span className="text-gray-500">Propriétaire :</span> <b>{prop.owner_name}</b>{prop.owner_company ? ` (${prop.owner_company})` : ''}</div>
          <div><span className="text-gray-500">Contact :</span> {prop.owner_email || '—'} {prop.owner_phone ? `· ${prop.owner_phone}` : ''}</div>
          <div><span className="text-gray-500">Lot :</span> {[prop.lot_label, prop.property_type, prop.surface_m2 ? `${prop.surface_m2} m²` : null].filter(Boolean).join(' — ') || '—'}{prop.zone_tendue ? ' · Zone tendue' : ''}</div>
          <div><span className="text-gray-500">Loyer CC :</span> <b>{eur(prop.rent_amount)}</b> (dont {eur(prop.charges_amount)} charges)</div>
          <div><span className="text-gray-500">Honoraires :</span> {prop.no_management ? <b className="text-amber-600">Bien sans gestion (quittances seules)</b> : <>{prop.mgmt_pct}% {prop.pct_mode === 'ttc' ? 'TTC' : 'HT'} — min {eur(prop.mgmt_min)} TTC/mois</>}</div>
          <div><span className="text-gray-500">GLI :</span> {prop.no_management ? '—' : prop.gli_enabled ? `souscrite (${prop.gli_rate_pct}% ${prop.pct_mode === 'ttc' ? 'TTC' : 'HT'})` : 'non souscrite'}</div>
          <div><span className="text-gray-500">Paiement :</span> {prop.payment_mode === 'prelevement' ? 'Prélèvement' : 'Virement'} (échéance le {prop.payment_day})</div>
          <div><span className="text-gray-500">Début gestion :</span> {prop.start_date || '—'}</div>
          <div><span className="text-gray-500">IRL réf. :</span> {prop.irl_ref || '—'}</div>
          <div><span className="text-gray-500">IBAN :</span> {prop.owner_iban || <span className="text-orange-500">manquant (requis pour export SEPA)</span>}</div>
          <div><span className="text-gray-500">Avis d'échéance :</span> {prop.notice_enabled === false ? 'désactivé' : `${prop.notice_days_before ?? 3} jours avant`}</div>
          {prop.charges_detail && <div className="col-span-2"><span className="text-gray-500">Charges :</span> {prop.charges_detail}</div>}
        </div>
      )}
      {!editing && (prop.revisions || []).length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3" data-testid="revisions-history">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Historique des révisions de loyer (IRL)</p>
          <div className="space-y-1">
            {[...prop.revisions].reverse().map(r => (
              <div key={r.revision_id} className="flex items-center justify-between text-xs py-1" data-testid={`revision-row-${r.revision_id}`}>
                <span>{(r.date || '').slice(0, 10).split('-').reverse().join('/')} — {eur(r.old_rent_hc)} → <b>{eur(r.new_rent_hc)}</b> HC ({r.pct >= 0 ? '+' : ''}{r.pct} %) · effet {r.effective_date ? r.effective_date.split('-').reverse().join('/') : '—'} · {r.irl_new_quarter}</span>
                {r.doc_id && <a href={`${API}/gestion/documents/${r.doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-[#008A8D] hover:underline" data-testid={`revision-letter-${r.revision_id}`}>Courrier{r.letter_sent ? ' ✉️' : ''}</a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TenantSection = ({ prop, onSaved }) => {
  const t = prop.tenant || {};
  const [form, setForm] = useState({ ...t });
  const [editing, setEditing] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const r = await axios.put(`${API}/gestion/properties/${prop.property_id}`, {
        tenant: {
          first_name: form.first_name || '', last_name: form.last_name || '',
          email: form.email || '', phone: form.phone || '',
          entry_date: form.entry_date || '', lease_date: form.lease_date || '',
          guarantor_name: form.guarantor_name || '', guarantor_address: form.guarantor_address || '',
          rent_amount: form.rent_amount ? parseFloat(form.rent_amount) : null,
          deposit: form.deposit ? parseFloat(form.deposit) : null,
        },
      });
      toast.success('Locataire mis à jour');
      setEditing(false); onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="realease-card p-6" data-testid="tenant-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Locataire</h3>
        <button onClick={() => editing ? save() : setEditing(true)} className="text-sm font-semibold text-[#008A8D]" data-testid="edit-tenant-btn">
          {editing ? 'Enregistrer' : 'Modifier'}
        </button>
      </div>
      {editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={label}>Prénom</label><input className={input} value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} data-testid="tenant-edit-first-name" /></div>
          <div><label className={label}>Nom</label><input className={input} value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} data-testid="tenant-edit-last-name" /></div>
          <div><label className={label}>Email</label><input className={input} value={form.email || ''} onChange={e => set('email', e.target.value)} data-testid="tenant-edit-email" /></div>
          <div><label className={label}>Téléphone</label><input className={input} value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
          <div><label className={label}>Date d'entrée</label><input type="date" className={input} value={form.entry_date || ''} onChange={e => set('entry_date', e.target.value)} /></div>
          <div><label className={label}>Date de signature du bail</label><input type="date" className={input} value={form.lease_date || ''} onChange={e => set('lease_date', e.target.value)} data-testid="edit-tenant-lease-date" /></div>
          <div><label className={label}>Loyer du bail (€)</label><input type="number" className={input} value={form.rent_amount || ''} onChange={e => set('rent_amount', e.target.value)} /></div>
          <div><label className={label}>Dépôt de garantie (€)</label><input type="number" className={input} value={form.deposit || ''} onChange={e => set('deposit', e.target.value)} /></div>
          <div><label className={label}>Garant / caution (nom)</label><input className={input} value={form.guarantor_name || ''} onChange={e => set('guarantor_name', e.target.value)} data-testid="edit-guarantor-name" /></div>
          <div><label className={label}>Adresse du garant</label><input className={input} value={form.guarantor_address || ''} onChange={e => set('guarantor_address', e.target.value)} data-testid="edit-guarantor-address" /></div>
        </div>
      ) : (t.first_name || t.last_name) ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><span className="text-gray-500">Nom :</span> <b>{t.first_name} {t.last_name}</b></div>
          <div><span className="text-gray-500">Contact :</span> {t.email || '—'} {t.phone ? `· ${t.phone}` : ''}</div>
          <div><span className="text-gray-500">Entrée :</span> {t.entry_date || '—'}</div>
          <div><span className="text-gray-500">Bail signé le :</span> {t.lease_date || '—'}</div>
          <div><span className="text-gray-500">Dépôt de garantie :</span> {t.deposit ? eur(t.deposit) : '—'}</div>
          <div><span className="text-gray-500">Garant :</span> {t.guarantor_name ? `${t.guarantor_name}${t.guarantor_address ? ' — ' + t.guarantor_address : ''}` : '—'}</div>
        </div>
      ) : <p className="text-sm text-gray-400">Aucun locataire renseigné. Cliquez sur "Modifier" pour l'ajouter.</p>}
    </div>
  );
};

const MandatesSection = ({ propertyId, mandates, leases = [], onRefresh }) => {
  const [creating, setCreating] = useState(false);
  const [showMelForm, setShowMelForm] = useState(false);
  const [mel, setMel] = useState({ honoraires_mode: 'montant', honoraires_value: '', honoraires_detail: '', duration_months: 12, send_now: false });
  const sendLease = async (type) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/gestion/properties/${propertyId}/leases`, { type, send_email: true });
      toast.success(r.data.email_sent
        ? `Bail ${r.data.number} créé et lien de signature envoyé au locataire`
        : `Bail ${r.data.number} créé (email non envoyé : email locataire manquant) — copiez le lien`);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };
  const createMandate = async (type) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/gestion/properties/${propertyId}/mandates`, { type, send_email: true });
      toast.success(r.data.email_sent
        ? 'Mandat créé et lien de signature envoyé par email au propriétaire'
        : 'Mandat créé (email non envoyé : email propriétaire manquant ou erreur)');
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };
  const createMelMandate = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/gestion/properties/${propertyId}/mandates`, {
        type: 'mise_en_location',
        send_email: mel.send_now,
        honoraires_mode: mel.honoraires_value === '' ? null : mel.honoraires_mode,
        honoraires_value: mel.honoraires_value === '' ? null : parseFloat(mel.honoraires_value),
        honoraires_detail: mel.honoraires_detail.trim(),
        duration_months: parseInt(mel.duration_months, 10) || 12,
      });
      toast.success(mel.send_now
        ? (r.data.email_sent ? `Mandat ${r.data.number} créé et envoyé pour signature au propriétaire` : 'Mandat créé (email non envoyé : email propriétaire manquant)')
        : `Mandat ${r.data.number} créé — vérifiez le PDF puis cliquez « Envoyer pour signature »`);
      setShowMelForm(false);
      setMel({ honoraires_mode: 'montant', honoraires_value: '', honoraires_detail: '', duration_months: 12, send_now: false });
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };
  const sendForSignature = async (mandateId) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/gestion/mandates/${mandateId}/send`);
      toast.success(r.data.email_sent ? `Lien de signature envoyé à ${r.data.to}` : "Échec de l'envoi email");
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/sign-gestion/${token}`)
      .then(() => toast.success('Lien de signature copié'));
  };
  return (
    <div className="realease-card p-6" data-testid="mandates-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900">Mandats</h3>
        <div className="flex gap-2">
          <button onClick={() => createMandate('gestion')} disabled={creating}
            className="px-3 py-1.5 text-xs font-semibold bg-[#008A8D] text-white rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="create-mandate-gestion-btn">
            + Mandat de gestion
          </button>
          <button onClick={() => setShowMelForm(s => !s)} disabled={creating}
            className="px-3 py-1.5 text-xs font-semibold border border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/5 disabled:opacity-50" data-testid="create-mandate-location-btn">
            {showMelForm ? 'Annuler' : '+ Mandat de mise en location'}
          </button>
        </div>
      </div>
      {showMelForm && (
        <div className="rounded-lg border border-[#008A8D]/30 bg-[#008A8D]/5 p-4 mb-4 space-y-3" data-testid="mel-mandate-form">
          <p className="text-sm font-semibold text-gray-800">Mandat de mise en location (recherche + installation du locataire, sans gestion locative)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-500 font-semibold">Honoraires</label>
              <select value={mel.honoraires_mode} onChange={e => setMel({ ...mel, honoraires_mode: e.target.value })}
                      className="realease-input text-sm w-full" data-testid="mel-honoraires-mode">
                <option value="montant">Montant fixe (€ TTC)</option>
                <option value="pct">% du loyer annuel CC</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-500 font-semibold">{mel.honoraires_mode === 'montant' ? 'Montant € TTC' : '% du loyer annuel'}</label>
              <input type="number" placeholder={mel.honoraires_mode === 'montant' ? 'Ex. 650' : 'Ex. 8'} value={mel.honoraires_value}
                     onChange={e => setMel({ ...mel, honoraires_value: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="mel-honoraires-value" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-500 font-semibold">Durée (mois)</label>
              <input type="number" value={mel.duration_months}
                     onChange={e => setMel({ ...mel, duration_months: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="mel-duration" />
            </div>
          </div>
          <input type="text" placeholder="Précision honoraires (optionnel, ex. « dont rédaction du bail et état des lieux d'entrée »)"
                 value={mel.honoraires_detail} onChange={e => setMel({ ...mel, honoraires_detail: e.target.value })}
                 className="realease-input text-sm w-full" data-testid="mel-honoraires-detail" />
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={mel.send_now} onChange={e => setMel({ ...mel, send_now: e.target.checked })}
                   data-testid="mel-send-now" />
            Envoyer immédiatement le lien de signature au propriétaire (sinon : créer, vérifier le PDF, puis envoyer)
          </label>
          <button onClick={createMelMandate} disabled={creating}
                  className="w-full py-2 text-sm font-semibold bg-[#008A8D] text-white rounded-lg hover:bg-[#006B6D] disabled:opacity-50"
                  data-testid="mel-create-btn">
            {creating ? 'Création…' : (mel.send_now ? 'Créer et envoyer pour signature électronique' : 'Créer le mandat (aperçu avant envoi)')}
          </button>
        </div>
      )}
      {mandates.length === 0 ? <p className="text-sm text-gray-400">Aucun mandat. Créez-en un : il sera pré-rempli avec les données du bien et envoyé pour signature électronique.</p> : (
        <div className="space-y-2">
          {mandates.map(m => (
            <div key={m.mandate_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg flex-wrap gap-2" data-testid={`mandate-row-${m.mandate_id}`}>
              <div>
                <p className="text-sm font-medium">{m.type === 'gestion' ? 'Mandat de gestion' : 'Mandat de mise en location'} <span className="text-gray-400">— {m.number}</span></p>
                <p className="text-xs text-gray-500">{m.status === 'signed' ? `Signé par ${m.signed_by} le ${(m.signed_at || '').slice(0, 10)}` : 'En attente de signature'}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {m.status === 'signed' ? 'Signé' : 'Envoyé'}
                </span>
                {m.status !== 'signed' && m.signature_token && (
                  <>
                    <button onClick={() => sendForSignature(m.mandate_id)} disabled={creating}
                      className="text-xs text-white bg-[#008A8D] hover:bg-[#006B6D] px-2 py-1 rounded font-medium disabled:opacity-50"
                      data-testid={`send-mandate-${m.mandate_id}`}>✍️ Envoyer pour signature</button>
                    <button onClick={() => copyLink(m.signature_token)} className="text-xs text-[#008A8D] font-medium hover:underline" data-testid={`copy-sign-link-${m.mandate_id}`}>Copier le lien</button>
                  </>
                )}
                <a href={`${API}/gestion/mandates/${m.mandate_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-gray-600 hover:underline" data-testid={`mandate-pdf-${m.mandate_id}`}>PDF</a>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Bail automatique (pré-rempli avec le bien et le locataire)</p>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API}/gestion/properties/${propertyId}/lease?type=meuble`} target="_blank" rel="noreferrer"
            className="px-3 py-1.5 text-xs font-semibold border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="lease-meuble-btn">
            📄 Bail location meublée (dépôt 2 mois HC)
          </a>
          <a href={`${API}/gestion/properties/${propertyId}/lease?type=nu`} target="_blank" rel="noreferrer"
            className="px-3 py-1.5 text-xs font-semibold border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="lease-nu-btn">
            📄 Bail location nue (dépôt 1 mois HC)
          </a>
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          <button onClick={() => sendLease('meuble')} disabled={creating} className="px-3 py-1.5 text-xs font-semibold bg-[#008A8D] text-white rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="send-lease-meuble-btn">
            ✍️ Faire signer le bail meublé
          </button>
          <button onClick={() => sendLease('nu')} disabled={creating} className="px-3 py-1.5 text-xs font-semibold bg-[#008A8D] text-white rounded-lg hover:bg-[#006B6D] disabled:opacity-50" data-testid="send-lease-nu-btn">
            ✍️ Faire signer le bail nu
          </button>
        </div>
        {leases.length > 0 && (
          <div className="space-y-2 mt-3" data-testid="leases-list">
            {leases.map(l => (
              <div key={l.lease_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg flex-wrap gap-2" data-testid={`lease-row-${l.lease_id}`}>
                <div>
                  <p className="text-sm font-medium">Bail {l.type === 'meuble' ? 'meublé' : 'nu'} <span className="text-gray-400">— {l.number}</span></p>
                  <p className="text-xs text-gray-500">{l.status === 'signed' ? `Signé par ${l.signed_by} le ${(l.signed_at || '').slice(0, 10)} — archivé dans le Drive` : 'En attente de signature du locataire'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {l.status === 'signed' ? 'Signé' : 'Envoyé'}
                  </span>
                  {l.status !== 'signed' && l.signature_token && (
                    <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sign-gestion/${l.signature_token}`).then(() => toast.success('Lien copié'))}
                      className="text-xs text-[#008A8D] font-medium hover:underline" data-testid={`copy-lease-link-${l.lease_id}`}>Copier le lien</button>
                  )}
                  {l.signed_doc_id && <a href={`${API}/gestion/documents/${l.signed_doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-gray-600 hover:underline" data-testid={`lease-signed-pdf-${l.lease_id}`}>PDF signé</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TicketsSection = ({ propertyId, tickets, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState('travaux');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [commentInputs, setCommentInputs] = useState({});

  const create = async () => {
    if (!title.trim()) { toast.error('Titre obligatoire'); return; }
    try {
      await axios.post(`${API}/gestion/properties/${propertyId}/tickets`, { type, title, comment });
      toast.success('Ticket créé');
      setShowForm(false); setTitle(''); setComment(''); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const setStatus = async (ticketId, status) => {
    await axios.put(`${API}/gestion/tickets/${ticketId}`, { status });
    onRefresh();
  };
  const addComment = async (ticketId) => {
    const text = commentInputs[ticketId];
    if (!text?.trim()) return;
    await axios.put(`${API}/gestion/tickets/${ticketId}`, { comment: text });
    setCommentInputs(c => ({ ...c, [ticketId]: '' }));
    onRefresh();
  };

  return (
    <div className="realease-card p-6" data-testid="tickets-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Travaux & Sinistres</h3>
        <button onClick={() => setShowForm(s => !s)} className="text-sm font-semibold text-[#008A8D]" data-testid="new-ticket-btn">+ Nouveau ticket</button>
      </div>
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
          <div className="flex gap-3">
            <select className={input + ' w-40'} value={type} onChange={e => setType(e.target.value)} data-testid="ticket-type-select">
              <option value="travaux">Travaux</option><option value="sinistre">Sinistre</option>
            </select>
            <input className={input} placeholder="Titre du ticket" value={title} onChange={e => setTitle(e.target.value)} data-testid="ticket-title-input" />
          </div>
          <input className={input} placeholder="Commentaire (optionnel)" value={comment} onChange={e => setComment(e.target.value)} />
          <button onClick={create} className="px-4 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg" data-testid="submit-ticket-btn">Créer</button>
        </div>
      )}
      {tickets.length === 0 ? <p className="text-sm text-gray-400">Aucun ticket en cours.</p> : (
        <div className="space-y-3">
          {tickets.map(t => (
            <div key={t.ticket_id} className="border border-gray-200 rounded-lg p-4" data-testid={`ticket-${t.ticket_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'sinistre' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    {t.type === 'sinistre' ? 'Sinistre' : 'Travaux'}
                  </span>
                  <b className="text-sm">{t.title}</b>
                </div>
                <button onClick={() => setStatus(t.ticket_id, t.status === 'resolu' ? 'en_cours' : 'resolu')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${t.status === 'resolu' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
                  data-testid={`ticket-status-btn-${t.ticket_id}`}>
                  {t.status === 'resolu' ? 'Résolu ✓' : 'En cours — marquer résolu'}
                </button>
              </div>
              {(t.comments || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {t.comments.map((c, i) => (
                    <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1"><b>{c.author}</b> · {(c.at || '').slice(0, 10)} — {c.text}</p>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <input className={input} placeholder="Ajouter un commentaire..." value={commentInputs[t.ticket_id] || ''}
                  onChange={e => setCommentInputs(c => ({ ...c, [t.ticket_id]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addComment(t.ticket_id)} data-testid={`ticket-comment-input-${t.ticket_id}`} />
                <button onClick={() => addComment(t.ticket_id)} className="px-3 py-1.5 text-xs bg-gray-100 rounded-lg hover:bg-gray-200">Ajouter</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function GestionPropertyDetailPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRent, setShowRent] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showRevision, setShowRevision] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gestion/properties/${propertyId}`);
      setData(r.data);
    } catch { toast.error('Bien non trouvé'); }
    finally { setLoading(false); }
  }, [propertyId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Annuler cet encaissement ? Les documents associés seront supprimés.')) return;
    await axios.delete(`${API}/gestion/payments/${paymentId}`);
    toast.success('Encaissement annulé');
    fetchData();
  };

  if (loading) return <div className="p-8 text-gray-400">Chargement...</div>;
  if (!data) return <div className="p-8 text-gray-400">Bien non trouvé.</div>;

  const { property: prop, payments, tickets, mandates, invoices, leases } = data;
  const st = STATUS_BADGE[data.rent_status] || STATUS_BADGE.attente;

  return (
    <div className="p-6 lg:p-8" data-testid="property-detail-page">
      <button onClick={() => navigate('/gestion')} className="text-sm text-gray-500 hover:text-[#008A8D] mb-4" data-testid="back-to-gestion-btn">← Retour à la gestion locative</button>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{prop.address}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-gray-600 text-sm">{prop.owner_name}{prop.owner_company ? ` (${prop.owner_company})` : ''}</span>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.cls}`} data-testid="rent-status-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>{st.label} (mois en cours)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowRevision(true)} className="px-4 py-3 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50" data-testid="rent-revision-btn">
            📈 Réviser le loyer (IRL)
          </button>
          <a href={`${API}/gestion/properties/${propertyId}/annual-report?year=${new Date().getFullYear()}`} target="_blank" rel="noreferrer" className="px-4 py-3 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50" data-testid="annual-report-link">
            📊 Relevé annuel
          </a>
          {prop.drive_folder_url && (
            <a href={prop.drive_folder_url} target="_blank" rel="noreferrer" className="px-4 py-3 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50" data-testid="drive-folder-link">
              📁 Dossier Drive
            </a>
          )}
          <button onClick={() => setShowRent(true)} className="px-6 py-3 bg-[#008A8D] text-white font-semibold rounded-lg hover:bg-[#006B6D] shadow-sm" data-testid="rent-received-btn">
            💶 Loyer encaissé
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <EditSection prop={prop} onSaved={p => setData(d => ({ ...d, property: p }))} />
        <TenantSection prop={prop} onSaved={p => setData(d => ({ ...d, property: p }))} />
      </div>

      <div className="realease-card p-6 mb-6" data-testid="payments-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900">Historique des encaissements</h3>
          <button onClick={() => setShowInvoice(true)} className="px-3 py-1.5 text-xs font-semibold border border-[#A57F4F] text-[#A57F4F] rounded-lg hover:bg-[#A57F4F]/5" data-testid="oneoff-invoice-btn">
            + Facturer des honoraires
          </button>
        </div>
        {payments.length === 0 ? <p className="text-sm text-gray-400">Aucun encaissement enregistré.</p> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                <th className="py-2 pr-4">Période</th><th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4 text-right">Loyer reçu</th><th className="py-2 pr-4 text-right">Honoraires</th>
                <th className="py-2 pr-4 text-right">Net versé</th><th className="py-2">Documents</th><th></th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.payment_id} className="border-b border-gray-50" data-testid={`payment-row-${p.payment_id}`}>
                  <td className="py-2.5 pr-4 font-medium">{periodLabel(p.period)}
                    {p.date_from && p.date_to && <span className="block text-[10px] text-gray-400">du {p.date_from.split('-').reverse().join('/')} au {p.date_to.split('-').reverse().join('/')}</span>}
                  </td>
                  <td className="py-2.5 pr-4 text-gray-500">{p.date_received}</td>
                  <td className="py-2.5 pr-4 text-right">{eur(p.rent_received)}</td>
                  <td className="py-2.5 pr-4 text-right text-red-600">- {eur(p.fees)}{p.gli_fee ? <span className="block text-[10px] text-orange-500">GLI - {eur(p.gli_fee)}</span> : null}</td>
                  <td className="py-2.5 pr-4 text-right font-semibold text-[#008A8D]">{eur(p.net_owner)}</td>
                  <td className="py-2.5">
                    <div className="flex gap-2">
                      {p.quittance_doc_id && <a href={`${API}/gestion/documents/${p.quittance_doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline" data-testid={`quittance-link-${p.payment_id}`}>Quittance{p.quittance_sent ? ' ✉️' : ''}</a>}
                      {p.report_doc_id && <a href={`${API}/gestion/documents/${p.report_doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline" data-testid={`report-link-${p.payment_id}`}>Compte rendu{p.report_sent ? ' ✉️' : ''}</a>}
                      {p.invoice_doc_id && <a href={`${API}/gestion/documents/${p.invoice_doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-[#A57F4F] hover:underline" data-testid={`invoice-link-${p.payment_id}`}>Facture {p.invoice_number || ''}</a>}
                    </div>
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditPayment(p)} className="text-xs text-[#008A8D] hover:underline mr-3" data-testid={`edit-payment-${p.payment_id}`}>Modifier</button>
                    <button onClick={() => deletePayment(p.payment_id)} className="text-xs text-gray-400 hover:text-red-500" data-testid={`delete-payment-${p.payment_id}`}>Annuler</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {(invoices || []).length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3" data-testid="invoices-list">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Factures d'honoraires émises</p>
            <div className="space-y-1">
              {invoices.map(inv => (
                <div key={inv.number} className="flex items-center justify-between text-sm py-1" data-testid={`invoice-row-${inv.number}`}>
                  <span><b className="text-[#A57F4F]">{inv.number}</b> — {inv.kind === 'oneoff' ? (inv.label || 'Honoraires libres') : `Gestion ${periodLabel(inv.period)}`}</span>
                  <span className="flex items-center gap-3">
                    <b>{eur(inv.total_ttc)} TTC</b>
                    {inv.doc_id && <a href={`${API}/gestion/documents/${inv.doc_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline">PDF</a>}
                    {inv.kind === 'oneoff' && <button onClick={async () => { if (window.confirm('Supprimer cette facture ?')) { await axios.delete(`${API}/gestion/invoices/${inv.number}`); fetchData(); } }} className="text-xs text-gray-400 hover:text-red-500" data-testid={`delete-invoice-${inv.number}`}>Suppr.</button>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MandatesSection propertyId={propertyId} mandates={mandates} leases={leases || []} onRefresh={fetchData} />
        <TicketsSection propertyId={propertyId} tickets={tickets} onRefresh={fetchData} />
      </div>

      {showRent && <RentModal prop={prop} onClose={() => setShowRent(false)} onDone={() => { setShowRent(false); fetchData(); }} />}
      {editPayment && <RentModal prop={prop} payment={editPayment} onClose={() => setEditPayment(null)} onDone={() => { setEditPayment(null); fetchData(); }} />}
      {showInvoice && <OneOffInvoiceModal prop={prop} onClose={() => setShowInvoice(false)} onDone={() => { setShowInvoice(false); fetchData(); }} />}
      {showRevision && <RevisionModal prop={prop} onClose={() => setShowRevision(false)} onDone={(p) => { setShowRevision(false); setData(d => ({ ...d, property: p })); }} />}
    </div>
  );
}
